import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
	CategoryVideos,
	FilterableListController,
} from "../components/VideosList";
import { standardizeSubCategoryPath, usePlayerData } from "../usePlayerData";
import { resolveSubCategoryPathIds } from "../utils/subCategoryPath";
import type { FilterByGroup } from "./CategoryRender";

export const GroupVideos = ({
	groupFilter,
	title,
	ignoreCategories,
}: {
	groupFilter: FilterByGroup;
	title: string;
	ignoreCategories?: boolean;
}) => {
	const params = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const playerData = usePlayerData();
	const splat = params["*"] || "";
	const subCategoryPathIds = useMemo(() => {
		return splat
			.split("/")
			.map((part) => part.trim())
			.map((part) => part.replace(/^sc:/, "sc__"))
			.filter(Boolean);
	}, [splat]);
	const rawLeafId = subCategoryPathIds[subCategoryPathIds.length - 1];

	// Derived data may be undefined while loading. ALL hooks must run on every
	// render (guards live inside them) — early returns above any hook caused
	// React #300 crashes whenever a background refetch flipped the query state.
	const group = playerData.query.data
		? playerData.queryGroupBy({
				id: groupFilter.groupId,
				name: groupFilter.groupName,
			})
		: undefined;
	const category = ignoreCategories
		? undefined
		: group?.categories.find((category) => category.id === params.category);

	const resolvedPath = useMemo(
		() =>
			resolveSubCategoryPathIds(category?.subCategories, subCategoryPathIds),
		[category?.subCategories, subCategoryPathIds],
	);
	const selectedNames = useMemo(() => {
		if (!category || resolvedPath.ids.length === 0) return [];

		const names: string[] = [];
		let currentList = category.subCategories;

		for (const id of resolvedPath.ids) {
			const found = currentList.find((item) => item.id === id);
			if (!found) break;
			names.push(found.name);
			currentList = found.subCategories || [];
		}

		return names;
	}, [category, resolvedPath.ids]);
	const selectedLeafId = resolvedPath.ids[resolvedPath.ids.length - 1];
	const normalizedPath = resolvedPath.ids.join("/");
	const rawPath = subCategoryPathIds.join("/");
	const basePath = useMemo(() => {
		const segments = location.pathname.split("/").filter(Boolean);
		const categoryIndex = segments.indexOf(params.category || "");
		if (categoryIndex === -1) return location.pathname;
		return `/${segments.slice(0, categoryIndex + 1).join("/")}`;
	}, [location.pathname, params.category]);

	useEffect(() => {
		if (!category) {
			return;
		}

		if (resolvedPath.isValid || resolvedPath.ids.length === 0) {
			if (!resolvedPath.isValid && rawPath) {
				navigate(basePath, { replace: true });
			}
			return;
		}

		if (normalizedPath !== rawPath) {
			navigate(`${basePath}/${normalizedPath}`, { replace: true });
		}
	}, [
		category,
		basePath,
		navigate,
		normalizedPath,
		rawPath,
		resolvedPath.ids.length,
		resolvedPath.isValid,
	]);

	// isLoading (not isFetching): background refetches must keep showing the
	// current content instead of flashing the loading screen every 20 minutes
	if (playerData.query.isLoading) {
		return (
			<div className="h-screen flex items-center justify-center">
				<div className="text-white">Carregando...</div>
			</div>
		);
	}

	if (playerData.query.isError || !playerData.query.data) {
		return (
			<div className="h-screen flex items-center justify-center">
				<div className="text-white">Erro ao carregar dados</div>
			</div>
		);
	}

	if (!group) {
		return (
			<div className="h-screen flex items-center justify-center">
				<div className="text-white">Grupo não encontrado</div>
			</div>
		);
	}

	if (ignoreCategories) {
		return (
			<FilterableListController
				breadcrumb={[title]}
				groupFilter={groupFilter}
			/>
		);
	}

	if (!category) {
		return (
			<div className="h-screen flex items-center justify-center">
				<div className="text-white">Categoria não encontrada</div>
			</div>
		);
	}

	const fullBreadcrumb = [title, category.name, ...selectedNames];
	const subCategoryPaths = [
		standardizeSubCategoryPath([category.name]),
		selectedNames.length > 0
			? standardizeSubCategoryPath([category.name, ...selectedNames])
			: null,
	].filter(Boolean) as string[];

	return (
		<FilterableListController
			breadcrumb={fullBreadcrumb}
			groupFilter={groupFilter}
			categoryId={category.id}
			subCategoryId={selectedLeafId || rawLeafId}
			subCategoryPaths={subCategoryPaths}
		>
			<CategoryVideos
				groupFilter={groupFilter}
				categoryId={category.id}
				breadcrumb={fullBreadcrumb}
				subCategoryPathIds={resolvedPath.ids}
			/>
		</FilterableListController>
	);
};
